import { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChatInput } from "../../../components/ui/ChatInput";
import { cn } from "../../../lib/utils";
import { usePageLinks } from "../../../domains/app/hooks/usePageLinks";
import { iosGenerateHaptic } from "../../../tools/ios";
import { HapticEffect } from "../../../interfaces";
import type { ArticlePageArticle } from "../hooks/useArticleData";
import { VisualAbstractPill } from "../../visual-abstract/VisualAbstractPill";
import {
  chatGroundingPlaceholder,
  useArticleChatGrounding,
} from "../../../domains/gpt/hooks/usePaperGrounding";
import { useDictationControl } from "../../../domains/gpt/hooks/useDictationControl";

interface ArticlePageChatBarProps {
  article: ArticlePageArticle;
  isTrackPlaying: boolean;
  // `fixed`: pinned to the bottom of the phone screen, over the safe area.
  // `docked`: sticks to the bottom of the desktop reading column it ends, so it
  // keeps the column's width instead of spanning the window.
  placement: "fixed" | "docked";
  /** The reader leaves the article for the chat (its exit, for analytics). */
  onOpenChat: () => void;
}

/**
 * The composer's question, and the textarea the dictation inserts at.
 */
function useArticleChatComposer(goToChat: (question: string) => void) {
  const [question, setQuestion] = useState("");
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const dictation = useDictationControl({
    onTranscript: setQuestion,
    textareaRef: composerRef,
    onSend: () => {
      goToChat(question);
      setQuestion("");
    },
    canSend: () => question !== "",
  });
  return { question, setQuestion, composerRef, dictation };
}

/**
 * Experimental bottom-docked composer for the article page. Hands the typed
 * question off to the Chat with Paper landing (auto-submitted there via
 * `initialQuestion` location state). Renders nothing when the article supports
 * neither full-text nor abstract chat, or while an audio track is active — the
 * docked AudioPlayer takes over the bottom slot.
 */
export function ArticlePageChatBar({
  article,
  isTrackPlaying,
  placement,
  onOpenChat,
}: ArticlePageChatBarProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  // Match the legacy chat button: chat-with-paper is not room-scoped.
  const { chatWithPaperLink } = usePageLinks(null);
  const { question, setQuestion, composerRef, dictation } =
    useArticleChatComposer(goToChat);

  const groundingKind = useArticleChatGrounding(article.slug);
  if (isTrackPlaying) return null;
  if (groundingKind === null) return null;

  function goToChat(initialQuestion: string): void {
    iosGenerateHaptic(HapticEffect.SELECTION);
    onOpenChat();
    history.push(chatWithPaperLink(article), { initialQuestion });
  }

  return (
    <div
      className={
        placement === "fixed"
          ? "fixed inset-x-0 bottom-0 z-10 px-5 pt-2.5"
          : // Figma "D3.explore.article": 674px, 18px from the column's
            // left edge and 28px from its right, 22px clear above and below.
            "sticky bottom-0 z-10 py-5.5 pr-7 pl-4.5"
      }
      // 11px read as flush against the screen edge on a browser, where the
      // safe area is 0.
      style={
        placement === "fixed"
          ? { paddingBottom: "calc(20px + var(--safe-area-bottom))" }
          : {}
      }
    >
      {placement === "fixed" ? (
        <ChatInput
          // Docked over the light article body: match the maquette's frosted
          // panel (warm translucent fill + visible border + drop shadow) so the
          // composer reads as a defined surface instead of blending into white.
          className={cn(
            "w-full",
            "border border-[rgba(11,17,16,0.14)]",
            "bg-[rgba(246,245,243,0.92)] backdrop-blur-sm",
            "shadow-[0px_8px_24px_rgba(11,17,16,0.1)]"
          )}
          placeholder={chatGroundingPlaceholder(t, groundingKind)}
          value={question}
          onChange={(e) => {
            setQuestion(e.target.value);
          }}
          mode={null}
          // The composer's button row is otherwise empty on this surface (no
          // mode picker, nothing to attach), so the pill has it to itself.
          leading={
            <VisualAbstractPill
              articleId={article.id}
              articleTitle={article.title}
              roomId={null}
            />
          }
          leadingPanel={null}
          banner={null}
          attach={null}
          dictation={null}
          stream={null}
          onSend={() => {
            goToChat(question);
            setQuestion("");
          }}
          sendDisabled={false}
        />
      ) : (
        // The desktop ChatInput "Mini" (Figma "D3.explore.article", ChatInput):
        // one row with the mic. The visual abstract is offered in the header
        // there, so the row has no pill.
        <ChatInput
          ref={composerRef}
          size="sm"
          className="w-full border border-ds-black/4 bg-ds-white/72"
          placeholder={t("Ask a follow-up…")}
          value={question}
          readOnly={dictation.composerReadOnly}
          onFocus={() => {
            if (dictation.isActive) dictation.stop();
          }}
          onChange={(e) => {
            setQuestion(e.target.value);
          }}
          mode={null}
          leading={null}
          leadingPanel={null}
          banner={null}
          attach={null}
          dictation={dictation.composer}
          stream={null}
          onSend={dictation.send}
          sendDisabled={false}
        />
      )}
    </div>
  );
}
